/* CustomerInvoicesCrudCard.tsx
    Une card affichant la gestion des factures clients
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
    Utilise l'API correspondante pour les opérations de données
*/

import React from 'react';
import { GenericCrudCard } from 'common';
import {
	CustomerInvoiceData,
	customerInvoicesApi,
} from 'features/customerInvoices';
import { faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface CustomerInvoicesCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

// CustomerInvoicesCrudCard component
export const CustomerInvoicesCrudCard: React.FC<
	CustomerInvoicesCrudCardProps
> = ({
	size = 'sm',
	returnPath = '/app/CustomerInvoices',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [{ label: 'ID', field: 'id', render: (a: CustomerInvoiceData) => a.id }]
			: []),
		// Colonne "Référence"
		{
			label: 'Référence',
			field: 'referenceNumber',
			render: (a: CustomerInvoiceData) => a.referenceNumber || '',
		},
		// Colonne "Affaire" — toujours visible, cliquable vers la fiche affaire
		{
			label: 'Affaire',
			field: 'project.referenceNumber',
			render: (a: CustomerInvoiceData) =>
				a.project ? a.project.referenceNumber : '—',
			exportValue: (a: CustomerInvoiceData) => a.project?.referenceNumber ?? '',
			linkUrl: (a: CustomerInvoiceData) =>
				a.project ? `/app/projects/${a.project.id}/edit` : '',
		},
		// Colonne "Montant HT" — calculé côté backend (activités + logistiques)
		{
			label: 'Montant HT',
			field: 'none',
			render: (a: CustomerInvoiceData) =>
				new Intl.NumberFormat('fr-FR', {
					style: 'currency',
					currency: 'EUR',
				}).format(Number(a.totalExclTax ?? 0)),
			exportValue: (a: CustomerInvoiceData) => Number(a.totalExclTax ?? 0),
		},
		// Colonne "Statut"
		{
			label: 'Statut',
			field: 'status',
			render: (a: CustomerInvoiceData) => a.status ?? '—',
			exportValue: (a: CustomerInvoiceData) => a.status ?? '',
		},
		// Colonne "Date de facture" au format jour/mois/année
		{
			label: 'Date',
			field: 'invoiceDate',
			filterType: 'date',
			render: (a: CustomerInvoiceData) =>
				a.invoiceDate
					? new Date(a.invoiceDate).toLocaleDateString('fr-FR')
					: '—',
			exportValue: (a: CustomerInvoiceData) =>
				a.invoiceDate
					? new Date(a.invoiceDate).toLocaleDateString('fr-FR')
					: '',
		},
		// Colonne "Date d'échéance" uniquement si extended
		...(extended
			? [
					{
						label: 'Échéance',
						field: 'dueDate',
						filterType: 'date',
						render: (a: CustomerInvoiceData) =>
							a.dueDate ? new Date(a.dueDate).toLocaleDateString('fr-FR') : '—',
						exportValue: (a: CustomerInvoiceData) =>
							a.dueDate ? new Date(a.dueDate).toLocaleDateString('fr-FR') : '',
					},
				]
			: []),
		// Colonne "Libellé" uniquement si extended
		...(extended
			? [
					{
						label: 'Libellé',
						field: 'label',
						render: (a: CustomerInvoiceData) => a.label || '—',
						exportValue: (a: CustomerInvoiceData) => a.label ?? '',
					},
				]
			: []),
	];

	// Colonnes export uniquement : données supplémentaires non affichées dans le tableau
	const exportOnlyColumns = [
		// Exercice fiscal
		{
			label: 'Exercice',
			exportValue: (a: CustomerInvoiceData) => a.fiscalYear ?? '',
		},
		// Créé par
		{
			label: 'Créé par',
			exportValue: (a: CustomerInvoiceData) =>
				a.createdBy
					? `${a.createdBy.firstName ?? ''} ${a.createdBy.name ?? ''}`.trim()
					: '',
		},
	];

	return (
		<GenericCrudCard<CustomerInvoiceData>
			queryKey="customerInvoices"
			title={size === 'sm' ? 'Factures clients' : 'Liste des factures clients'}
			fetchData={(token, options) =>
				customerInvoicesApi.listPaginated(token, {
					...options,
					sort: options?.sort || 'referenceNumber',
					order: options?.order || 'DESC',
				})
			}
			deleteItem={customerInvoicesApi.delete}
			tableColumns={columns}
			exportOnlyColumns={exportOnlyColumns}
			getAddPath={() => '/app/CustomerInvoices/new'}
			getEditPath={(id) => `/app/CustomerInvoices/${id}/edit`}
			emptyMessage="Aucune facture client trouvée."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faFileInvoiceDollar : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};
export default CustomerInvoicesCrudCard;
